import {
    centsToFormattedDollars,
    formattedDollarsToCents,
    roundCentsToWholeDollarCents,
} from '@fruitful/fruitils';
import { getCategorizedRulesAndTotals } from '@fruitful/money-map';
import {
    type DeepPartial,
    MoneyMapRuleType,
    type MoneyMapWithRulesAndAccounts,
    PaycheckRoutingRuleType,
} from '@fruitful/types';
import { useAtomValue } from 'jotai';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    DollarInput,
    LabelRegular,
    Slider,
    type StackProps,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { selectedMembershipAtom } from '../../entities/entityAtoms';
import { useMoneyMapAccounts } from '../providers/MoneyMapAccountsProvider';
import { getMoneyMapperEffectiveMonthlyIncomeCents } from '../utils/moneyMapperEffectiveIncome';
import { MoneyMapperSectionHeading } from './MoneyMapperSectionHeading';

const centsToDollarInputValue = (cents: number): string => {
    return centsToFormattedDollars(
        roundCentsToWholeDollarCents(cents),
        false,
    ).replace('$', '$ ');
};

interface EditableSliderInputProps {
    color: string;
    containerProps?: StackProps;
    disabled?: boolean;
    editValue: string;
    isEditing: boolean;
    label: string;
    onChangeText: (value: string) => void;
    onCommit: () => Promise<void>;
    onStartEditing: () => void;
    valueCents: number;
}

const EditableSliderInput = ({
    color,
    containerProps,
    disabled = false,
    editValue,
    isEditing,
    label,
    onChangeText,
    onCommit,
    onStartEditing,
    valueCents,
}: EditableSliderInputProps) => {
    const isRightAligned = containerProps?.alignItems === 'flex-end';

    return (
        <YStack
            minWidth={140}
            width={140}
            maxWidth={140}
            gap={4}
            borderRadius={16}
            cursor={disabled ? 'default' : 'pointer'}
            hoverStyle={
                isEditing || disabled
                    ? undefined
                    : { backgroundColor: '$gray100' }
            }
            onPress={isEditing || disabled ? undefined : onStartEditing}
            {...containerProps}
        >
            {isEditing ? (
                <DollarInput
                    value={editValue}
                    onChangeText={onChangeText}
                    onBlur={() => {
                        void onCommit();
                    }}
                    onSubmitEditing={() => {
                        void onCommit();
                    }}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    allowDecimals={false}
                    size="regular"
                    containerProps={{ width: '100%', maxWidth: '100%' }}
                    disabled={disabled}
                    textAlign={isRightAligned ? 'right' : 'left'}
                />
            ) : (
                <YStack padding={12} gap={4} borderRadius={16} width="100%">
                    <XStack
                        alignItems="center"
                        gap={8}
                        width="100%"
                        justifyContent={
                            isRightAligned ? 'flex-end' : 'flex-start'
                        }
                    >
                        <YStack
                            width={8}
                            height={8}
                            borderRadius={4}
                            backgroundColor={color}
                        />
                        <LabelRegular color="$gray1000">{label}</LabelRegular>
                    </XStack>
                    <BodyMediumEmphasized
                        color="$gray1200"
                        fontWeight="$2"
                        fontSize={18}
                        textAlign={isRightAligned ? 'right' : 'left'}
                        width="100%"
                    >
                        {centsToDollarInputValue(valueCents)}
                        /mo
                    </BodyMediumEmphasized>
                </YStack>
            )}
        </YStack>
    );
};

interface InputFieldCardProps {
    title: string;
    description: string;
    dotColor: string;
    value: string;
    onCommitText: (value: string) => Promise<void>;
    disabled?: boolean;
    isSaving?: boolean;
}

const InputFieldCard = ({
    title,
    description,
    dotColor,
    value,
    onCommitText,
    disabled = false,
    isSaving = false,
}: InputFieldCardProps) => {
    const [draftValue, setDraftValue] = useState(value);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (!isFocused) {
            setDraftValue(value);
        }
    }, [value, isFocused]);

    const commitValue = async () => {
        setIsFocused(false);

        if (disabled || isSaving || draftValue === value) {
            return;
        }

        await onCommitText(draftValue);
    };

    return (
        <YStack
            flex={1}
            minWidth={0}
            backgroundColor="$white"
            padding={24}
            gap={16}
            borderRadius={24}
            opacity={disabled ? 0.5 : 1}
            pointerEvents={disabled ? 'none' : 'auto'}
        >
            <YStack gap={4}>
                <XStack alignItems="center" gap={8} flexWrap="nowrap">
                    <View
                        width={8}
                        height={8}
                        borderRadius={4}
                        backgroundColor={dotColor}
                    />
                    <BodyMediumEmphasized color="$gray1200">
                        {title}
                    </BodyMediumEmphasized>
                </XStack>
                <BodySmallRegular color="$gray1000">
                    {description}
                </BodySmallRegular>
            </YStack>
            <DollarInput
                value={draftValue}
                allowDecimals={false}
                onChangeText={setDraftValue}
                onFocus={() => {
                    setIsFocused(true);
                }}
                onBlur={() => {
                    void commitValue();
                }}
                onSubmitEditing={() => {
                    void commitValue();
                }}
                disabled={disabled || isSaving}
            />
        </YStack>
    );
};

interface MoneyMapperInputCollectionSectionProps {
    isSaving: boolean;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onUpdateMoneyMap: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<boolean>;
    /** Same API as Take-home pay footer — writes `monthlyIncomeCents` without toggling main map save. */
    persistMonthlyIncomeCentsToMoneyMap: (
        monthlyIncomeCents: number,
    ) => Promise<boolean>;
}

export const MoneyMapperInputCollectionSection = ({
    isSaving,
    moneyMap,
    onUpdateMoneyMap,
    persistMonthlyIncomeCentsToMoneyMap,
}: MoneyMapperInputCollectionSectionProps) => {
    const membership = useAtomValue(selectedMembershipAtom);
    const { accounts } = useMoneyMapAccounts();
    // Holds the pending debounced slider save so a new drag can replace it.
    const sliderSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    // Tracks which side of the editable slider is currently showing an inline input.
    const [editingSliderInput, setEditingSliderInput] = useState<
        'spend' | 'goals' | null
    >(null);
    // Stores the formatted currency string while the inline input is being edited.
    const [editingSliderInputValue, setEditingSliderInputValue] =
        useState('$ 0');
    // Shows the spend value immediately while the slider moves before save completes.
    const [sliderSpendPreviewCents, setSliderSpendPreviewCents] = useState<
        number | null
    >(null);

    const { billsTotalCents, spendTotalCents, goalsTotalCents } =
        useMemo(() => {
            return getCategorizedRulesAndTotals(moneyMap, accounts);
        }, [accounts, moneyMap]);
    const monthlyIncomeCents = getMoneyMapperEffectiveMonthlyIncomeCents({
        moneyMap,
        membershipIncomes: membership?.membershipIncomes,
    });
    const monthlyBillsCents = billsTotalCents ?? 0;
    const hasBills = monthlyBillsCents > 0;
    const monthlySpendCents = spendTotalCents ?? 0;
    const displayedSpendCents = sliderSpendPreviewCents ?? monthlySpendCents;
    const leftForGoalsCents =
        sliderSpendPreviewCents == null
            ? Math.max(0, goalsTotalCents ?? 0)
            : Math.max(
                  0,
                  monthlyIncomeCents - monthlyBillsCents - displayedSpendCents,
              );
    const spendPercent = Math.round(
        (displayedSpendCents / (displayedSpendCents + leftForGoalsCents || 1)) *
            100,
    );

    const updateRule = useCallback(
        async (
            ruleType: MoneyMapRuleType.BILLS | MoneyMapRuleType.SPEND,
            amountCents: number,
        ) => {
            const rules = [...(moneyMap.moneyMapRules ?? [])];
            const ruleIndex = rules.findIndex((r) => r.type === ruleType);

            if (ruleIndex >= 0 && rules[ruleIndex]) {
                if (ruleType === MoneyMapRuleType.BILLS) {
                    // Monthly bills figure → targetBalanceCents. See PaycheckRoutingRuleType.
                    rules[ruleIndex] = {
                        ...rules[ruleIndex],
                        routingRuleType:
                            PaycheckRoutingRuleType.MINIMUM_BALANCE,
                        targetBalanceCents: amountCents,
                        // Explicitly null out PERCENTAGE-only fields -- some legacy
                        // rules have both shapes populated (one stale), and a partial
                        // update won't clear a field that's simply omitted.
                        monthlyContributionMaxCents: null,
                        yearlyContributionMaxCents: null,
                    };
                } else if (ruleType === MoneyMapRuleType.SPEND) {
                    rules[ruleIndex] = {
                        ...rules[ruleIndex],
                        routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
                        percentage: 1,
                        monthlyContributionMaxCents: amountCents,
                        // Explicitly null out the MINIMUM_BALANCE-only field -- see
                        // comment above.
                        targetBalanceCents: null,
                    };
                }

                await onUpdateMoneyMap({ moneyMapRules: rules });
            }
        },
        [moneyMap.moneyMapRules, onUpdateMoneyMap],
    );

    const parseDollarsToCents = (formattedValue: string): number => {
        const result = formattedDollarsToCents(formattedValue);
        return Number.isNaN(result) ? 0 : Math.max(0, result);
    };

    const parseWholeDollarsToCents = (formattedValue: string): number => {
        return roundCentsToWholeDollarCents(
            parseDollarsToCents(formattedValue),
        );
    };

    const handleIncomeChange = async (formattedValue: string) => {
        await persistMonthlyIncomeCentsToMoneyMap(
            parseWholeDollarsToCents(formattedValue),
        );
    };

    const handleBillsChange = async (formattedValue: string) => {
        await updateRule(
            MoneyMapRuleType.BILLS,
            parseWholeDollarsToCents(formattedValue),
        );
    };

    const handleSpendGoalsSliderChange = (value: number) => {
        if (isSaving) {
            return;
        }

        const remainder = Math.max(0, monthlyIncomeCents - monthlyBillsCents);
        const spendCents = roundCentsToWholeDollarCents(
            Math.round((value / 100) * remainder),
        );
        setSliderSpendPreviewCents(spendCents);

        if (sliderSaveTimeoutRef.current != null) {
            clearTimeout(sliderSaveTimeoutRef.current);
        }

        sliderSaveTimeoutRef.current = setTimeout(() => {
            void updateRule(MoneyMapRuleType.SPEND, spendCents);
        }, 300);
    };

    const startEditing = (field: 'spend' | 'goals') => {
        if (!hasBills || isSaving) {
            return;
        }

        setEditingSliderInput(field);
        setEditingSliderInputValue(
            centsToDollarInputValue(
                field === 'spend' ? displayedSpendCents : leftForGoalsCents,
            ),
        );
    };

    const commitSliderInputValue = async () => {
        if (editingSliderInput == null) {
            return;
        }

        const remainder = Math.max(0, monthlyIncomeCents - monthlyBillsCents);
        const enteredCents = Math.min(
            remainder,
            parseWholeDollarsToCents(editingSliderInputValue),
        );

        await updateRule(
            MoneyMapRuleType.SPEND,
            editingSliderInput === 'spend'
                ? enteredCents
                : remainder - enteredCents,
        );
        setEditingSliderInput(null);
    };

    useEffect(() => {
        if (!hasBills || isSaving) {
            setEditingSliderInput(null);
        }
    }, [hasBills, isSaving]);

    useEffect(() => {
        setSliderSpendPreviewCents(null);
    }, [monthlySpendCents]);

    useEffect(() => {
        return () => {
            if (sliderSaveTimeoutRef.current != null) {
                clearTimeout(sliderSaveTimeoutRef.current);
            }
        };
    }, []);

    return (
        <YStack flex={1} gap={24}>
            <MoneyMapperSectionHeading
                title="Spend"
                subtitleMarginBottom={12}
            />
            <XStack
                gap={24}
                width="100%"
                flexWrap="nowrap"
                alignItems="stretch"
            >
                <InputFieldCard
                    title="Monthly take-home pay"
                    description="Accounted in the monthly total."
                    dotColor="$wasabi500"
                    isSaving={isSaving}
                    value={centsToDollarInputValue(monthlyIncomeCents)}
                    onCommitText={handleIncomeChange}
                    disabled={isSaving}
                />
                <InputFieldCard
                    title="Bills"
                    description="Monthly recurring bills and debt."
                    dotColor="$dropletBlue500"
                    isSaving={isSaving}
                    value={centsToDollarInputValue(monthlyBillsCents)}
                    onCommitText={handleBillsChange}
                    disabled={isSaving}
                />
            </XStack>
            <YStack
                backgroundColor="$white"
                padding={24}
                gap={16}
                borderRadius={24}
                opacity={!hasBills ? 0.5 : 1}
                pointerEvents={!hasBills || isSaving ? 'none' : 'auto'}
            >
                <YStack gap={4}>
                    <BodyMediumRegular color="$gray1200" fontWeight="$2">
                        Spend & Goals
                    </BodyMediumRegular>
                    <BodySmallRegular color="$gray1000">
                        The split of leftover funds between day-to-day spending
                        and saving up for goals
                    </BodySmallRegular>
                </YStack>
                <XStack
                    alignItems="center"
                    gap={8}
                    paddingVertical={12}
                    width="100%"
                    flexWrap="nowrap"
                >
                    <EditableSliderInput
                        color="$wasabi500"
                        containerProps={{ alignItems: 'flex-start' }}
                        disabled={isSaving}
                        editValue={editingSliderInputValue}
                        isEditing={editingSliderInput === 'spend'}
                        label="Spend"
                        onChangeText={setEditingSliderInputValue}
                        onCommit={commitSliderInputValue}
                        onStartEditing={() => {
                            startEditing('spend');
                        }}
                        valueCents={displayedSpendCents}
                    />
                    <YStack flex={1} alignItems="center">
                        <Slider
                            disabled={isSaving}
                            value={[spendPercent]}
                            tone="positive"
                            trackColor="$peach500"
                            thumbColor="$white"
                            onSlideMove={(_, value: number) => {
                                handleSpendGoalsSliderChange(Math.round(value));
                            }}
                            min={0}
                            max={100}
                            step={1}
                            width="100%"
                        />
                    </YStack>
                    <EditableSliderInput
                        color="$peach500"
                        containerProps={{ alignItems: 'flex-end' }}
                        disabled={isSaving}
                        editValue={editingSliderInputValue}
                        isEditing={editingSliderInput === 'goals'}
                        label="Goals"
                        onChangeText={setEditingSliderInputValue}
                        onCommit={commitSliderInputValue}
                        onStartEditing={() => {
                            startEditing('goals');
                        }}
                        valueCents={leftForGoalsCents}
                    />
                </XStack>
            </YStack>
        </YStack>
    );
};
